import { Directive, ElementRef, HostListener, booleanAttribute, inject, input } from '@angular/core';
import {
  FOCUSABLE_CONTROL_SELECTOR,
  focusControl,
  getActiveElement,
  getTabStops,
  indexOfTabStop,
  isEditableControl,
  isPlainTab,
} from './focus-utils';

/**
 * Normalises `Tab` / `Shift+Tab` navigation inside a container so focus always moves to the next applicable editable
 * control in the visual and functional order of the page.
 *
 * Disabled, hidden and read-only controls are skipped. Standard browser behaviour is preserved at the boundaries of the
 * container: when the user tabs past the last (or before the first) control the event is left untouched so focus
 * escapes to the surrounding page as normal.
 *
 * Apply it to forms, dialogs, assessment sections and any other screen that needs a predictable tab sequence.
 *
 * @example
 *   ```html
 *   <form hxTabNav>
 *     ...
 *   </form>
 *
 *   <!-- Layouts whose visual order differs from the DOM order -->
 *   <div hxTabNav order="visual">...</div>
 *   ```;
 */
@Directive({
  selector: '[hxTabNav]',
  standalone: true,
  host: {
    '(keydown)': 'handleKeydown($event)',
    '(focusin)': 'handleFocusin($event)',
  },
})
export class HxTabNavigation {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private lastFocusedInside: HTMLElement | null = null;
  private lastInteractedElement: HTMLElement | null = null;

  /**
   * How the editable controls are ordered.
   *
   * - `dom` (default) follows the document order, which matches the visual order for normal flow layouts.
   * - `visual` sorts by on-screen geometry (top-to-bottom, left-to-right) and should be used for flex/grid layouts that
   *   reorder their children.
   */
  readonly order = input<'dom' | 'visual'>('dom', { alias: 'hxTabNavOrder' });

  /** Disables the managed behaviour and falls back to the browser's native tab sequence. */
  readonly disabled = input(false, { alias: 'hxTabNavDisabled', transform: booleanAttribute });

  /** When true, tabbing past the last control wraps back to the first one instead of leaving the container. */
  readonly wrap = input(false, { alias: 'hxTabNavWrap', transform: booleanAttribute });

  @HostListener('document:focusin', ['$event'])
  protected handleFocusin(event: FocusEvent): void {
    const target = event.target as HTMLElement | null;
    if (target && this.host.nativeElement.contains(target)) {
      this.lastInteractedElement = target;
      const isControl =
        target !== this.host.nativeElement &&
        target.matches(
          'input:not([type="hidden"]), select, textarea, button, a[href], [role="radio"], [role="checkbox"], [role="combobox"], [role="option"], [role="tab"], [role="switch"], [role="textbox"], [role="spinbutton"], [role="slider"]'
        ) &&
        isEditableControl(target, true);

      if (isControl) {
        this.lastFocusedInside = target;
      }
    }
  }

  @HostListener('document:mousedown', ['$event'])
  protected handleDocumentMouseDown(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target && this.host.nativeElement.contains(target)) {
      this.lastInteractedElement = target;
    } else {
      // User clicked outside this container: reset interaction tracking
      this.lastInteractedElement = null;
      this.lastFocusedInside = null;
    }
  }

  /**
   * Document-level Tab guard. When focus is lost to document.body (e.g. clicking an empty/non-interactive area inside
   * the container), this intercepts Tab / Shift+Tab so the natural flow within the container is preserved rather than
   * escaping to the top of the webpage.
   */
  @HostListener('document:keydown', ['$event'])
  protected handleDocumentKeydown(event: KeyboardEvent): void {
    if (this.disabled() || !isPlainTab(event) || event.defaultPrevented) {
      return;
    }

    const active = getActiveElement();
    const isInside = Boolean(active && this.host.nativeElement.contains(active));

    if (isInside) {
      return;
    }

    const isBodyOrNull = !active || active === document.body || active === document.documentElement;

    if (isBodyOrNull && (this.lastInteractedElement || this.lastFocusedInside)) {
      this.handleKeydown(event);
    }
  }

  /**
   * Resolves the enclosing subsection or section wrapper for an element to determine logical context within the
   * assessment.
   */
  private getSectionContext(element: HTMLElement | null): HTMLElement | null {
    if (!element || !this.host.nativeElement.contains(element)) {
      return null;
    }

    return (
      element.closest<HTMLElement>(
        'hx-subsection-control, [data-subsection-id], .hx-subsection-control, hx-sub-section, .hx-sub-section'
      ) ??
      element.closest<HTMLElement>(
        'hx-section-control, .hx-section-control, hx-section, .hx-section, .hx-assessment-renderer__section-wrapper, [data-section-index], section'
      )
    );
  }

  protected handleKeydown(event: KeyboardEvent): void {
    if (this.disabled() || !isPlainTab(event) || event.defaultPrevented) {
      return;
    }

    let active = getActiveElement();
    const isInside = Boolean(active && this.host.nativeElement.contains(active));

    // If focus was lost to document.body (e.g. clicked non-interactive area or control removed/disabled),
    // recover focus from the last interacted/clicked location or last known control inside this container.
    if (!isInside) {
      const isBodyOrNull = !active || active === document.body || active === document.documentElement;
      if (isBodyOrNull && (this.lastInteractedElement || this.lastFocusedInside)) {
        active = this.lastInteractedElement || this.lastFocusedInside;
      } else {
        return;
      }
    }
    const controls = getTabStops(this.host.nativeElement, this.order(), false, event.shiftKey);
    if (controls.length === 0) {
      return;
    }

    const isControl = Boolean(
      active &&
      active !== this.host.nativeElement &&
      active.matches(
        'input:not([type="hidden"]), select, textarea, button, a[href], [role="radio"], [role="checkbox"], [role="combobox"], [role="option"], [role="tab"], [role="switch"], [role="textbox"], [role="spinbutton"], [role="slider"]'
      ) &&
      isEditableControl(active, true)
    );

    if (isInside && isControl) {
      this.lastFocusedInside = active;
      this.lastInteractedElement = active;
    }

    // If returning from a blur or click on non-interactive space/container (e.g. whitespace, scroll container, or label),
    // and the user was previously focused on an active control in the same subsection/section/group,
    // restore focus directly to that last focused control (e.g. previously visited radio/checkbox).
    const canRestore =
      this.lastFocusedInside &&
      this.lastFocusedInside.isConnected &&
      this.host.nativeElement.contains(this.lastFocusedInside) &&
      !this.lastFocusedInside.hidden &&
      !(this.lastFocusedInside as any).disabled &&
      this.lastFocusedInside.getAttribute('aria-disabled') !== 'true' &&
      !this.lastFocusedInside.closest('[inert], [hidden], .hx-display-none');

    if (!isControl && canRestore) {
      const ctxInteracted = this.getSectionContext(this.lastInteractedElement);
      const ctxFocused = this.getSectionContext(this.lastFocusedInside);

      const sameContext =
        !this.lastInteractedElement ||
        !ctxInteracted ||
        !ctxFocused ||
        this.lastInteractedElement === this.lastFocusedInside ||
        this.lastInteractedElement.contains(this.lastFocusedInside!) ||
        this.lastFocusedInside!.contains(this.lastInteractedElement) ||
        ctxInteracted === ctxFocused ||
        ctxInteracted.contains(ctxFocused) ||
        ctxFocused.contains(ctxInteracted);

      if (sameContext) {
        event.preventDefault();
        const target = this.lastFocusedInside!;
        this.lastInteractedElement = target;
        focusControl(target);
        return;
      }
    }

    // If recovering from a non-interactive area click (e.g. whitespace, section div, or label),
    // enter the first control inside that container or following it in DOM tree order.
    const ref = !isInside || !isControl ? (this.lastInteractedElement ?? active) : null;
    if (ref && !controls.includes(ref)) {
      const targetIndex = controls.findIndex(
        (control) =>
          ref === control ||
          ref.contains(control) ||
          Boolean(ref.compareDocumentPosition(control) & Node.DOCUMENT_POSITION_FOLLOWING)
      );
      if (targetIndex !== -1) {
        const delta = event.shiftKey ? -1 : 0;
        let nextIndex = targetIndex + delta;
        if (nextIndex < 0 || nextIndex >= controls.length) {
          if (!this.wrap()) {
            return;
          }
          nextIndex = (nextIndex + controls.length) % controls.length;
        }
        const next = controls[nextIndex];
        if (next) {
          event.preventDefault();
          this.lastFocusedInside = next;
          this.lastInteractedElement = next;
          focusControl(next);
          return;
        }
      }
    }

    let currentIndex = indexOfTabStop(this.host.nativeElement, controls, active);
    if (currentIndex === -1) {
      // If the active control was removed or disabled (so it's no longer in controls),
      // find the closest subsequent stop based on DOM position.
      if (active) {
        currentIndex = controls.findIndex((control) =>
          Boolean(active!.compareDocumentPosition(control) & Node.DOCUMENT_POSITION_FOLLOWING)
        );
        if (currentIndex !== -1 && event.shiftKey) {
          currentIndex = Math.max(0, currentIndex - 1);
        }
      }
      if (currentIndex === -1) {
        if (canRestore) {
          event.preventDefault();
          this.lastInteractedElement = this.lastFocusedInside;
          focusControl(this.lastFocusedInside!);
          return;
        }
        if (this.wrap() && controls.length > 0) {
          event.preventDefault();
          const target = event.shiftKey ? controls[controls.length - 1] : controls[0];
          this.lastFocusedInside = target;
          this.lastInteractedElement = target;
          focusControl(target);
          return;
        }
        return;
      }

      const delta = event.shiftKey ? -1 : 0;
      let nextIndex = currentIndex + delta;

      if (nextIndex < 0 || nextIndex >= controls.length) {
        if (!this.wrap()) {
          return;
        }
        nextIndex = (nextIndex + controls.length) % controls.length;
      }

      const next = controls[nextIndex];
      if (!next) {
        return;
      }

      event.preventDefault();
      this.lastFocusedInside = next;
      this.lastInteractedElement = next;
      focusControl(next);
      return;
    }

    const delta = event.shiftKey ? -1 : 1;
    let nextIndex = currentIndex + delta;

    if (nextIndex < 0 || nextIndex >= controls.length) {
      if (!this.wrap()) {
        // Preserve standard behaviour: let the browser move focus out of the container.
        return;
      }
      nextIndex = (nextIndex + controls.length) % controls.length;
    }

    const next = controls[nextIndex];
    if (!next) {
      return;
    }

    event.preventDefault();
    this.lastFocusedInside = next;
    this.lastInteractedElement = next;
    focusControl(next);
  }

  /** Programmatically focus the first applicable editable control in the container. */
  focusFirst(): boolean {
    const [first] = getTabStops(this.host.nativeElement, this.order());
    if (!first) {
      return false;
    }
    focusControl(first);
    return true;
  }
}
